import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The only light is the sun behind the leaves.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'aspen',
  family: 'organic',
  danceability: 0.25,
  energyLevel: 0.24,
  bpmMin: 45,
  bpmMax: 130,
  movement: 0.40,
  tonality: 0.92,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.50] },
  enabled: true
};

// Aspen: a canopy of quaking aspens seen from below, lying on the ground and
// looking straight up, the sun behind the leaves.
//
// THE PIVOT, NOT THE SWAY. An aspen's petiole is flattened at right angles to
// the blade, so the lightest air TURNS the leaf about the petiole's axis - one
// way, then the other - showing its green face and then its pale silver back.
// Every leaf here does exactly that in the vertex shader:
//   angle(t) = A sin(2 pi f t + phase), about the petiole
// at its OWN frequency, 1.5 to 4 Hz at rest (aspen leaves are FAST - a leaf
// that moves slowly is another tree). The shimmer is thousands of leaves
// showing their backs at different instants. A gust is a travelling 2-D
// gaussian: where it passes the amplitude climbs to +-60 degrees and the
// frequency to 6 Hz, and the zone it crosses flashes silver.
//
// BACKLIGHT. Seen from below against the sun a leaf is lit by TRANSMISSION:
// facing the sun it glows golden green, edge-on it goes dark, and its back
// turned to us reads silver. Leaves near the sun's disc are nearly white.
//
// ONE TRACK IS ONE AUTUMN: the canopy yellows over the track, patch by patch,
// and by the end it is gold with leaves coming down.
//
// WORLD. The camera sits at the origin looking down -z; "up the tree" is
// deeper. Trunks start near the camera at the frame's edges and converge on
// the far centre (the upward perspective), stopping short of it - the cover
// sits there, where the canopy is densest and furthest. The camera turns
// slowly on its own axis, one revolution in 240 s.

// --- the tree ------------------------------------------------------------------
const TRUNKS = 6;
// THE CANOPY IS A TREE, NOT A CLOUD. Each trunk carries 3-5 main limbs, each
// limb 3-4 branches, each branch 4-6 twigs; leaves hang on the TWIGS only, in
// tight bunches. That is what makes masses of foliage with sky between them.
const MAINS_PER_TRUNK = [3, 5];
const SUBS_PER_MAIN = [3, 4];
const TWIGS_PER_SUB = [4, 6];
const MAX_MAINS = TRUNKS * MAINS_PER_TRUNK[1];   // 30 swaying limbs, the only CPU motion
const LEAVES_PER_TWIG = [15, 40];
const LEAF_SPREAD = 0.30;                // gaussian sigma around the twig, world units
const NEAR_Z = -2.2, FAR_Z = -34;
const TRUNK_END_ANGULAR = 0.30;          // trunks stop this far off the centre (in tan units)
const LEAF_SIZE = 0.46;                  // world units, blade length
// Shade leaves low in the crown are bigger than sun leaves at the top; with the
// depth this gives the brief's 6:1 between the near rim and the far centre.
const LEAF_SIZE_LOW = 1.25, LEAF_SIZE_HIGH = 0.85;
// Line widths, px, thinning out with the order: every limb, branch and twig
// is a curve of 3 pieces, and each piece takes a class -
//   limb 5 5 4, branch 3 3 2, twig 2 1 1.
const LINE_PX = [5, 4, 3, 2, 1];
const LIMB_CLASSES = [0, 0, 1], BRANCH_CLASSES = [2, 2, 3], TWIG_CLASSES = [3, 4, 4];
// ?aspenbranches=1: each tree in its own colour, to see any line crossing trees.
const DEBUG_TREE_COLORS = [0xff3b30, 0xffcc00, 0x34c759, 0x00c7ff, 0x5856d6, 0xff2dff];
const CAMERA_TURN_S = 240;

// --- the wind ------------------------------------------------------------------
const BASE_AMP_DEG = 8;
const GUST_AMP_DEG = 60;
const FREQ_REST = [1.5, 4.0];            // Hz
const FREQ_GUST = 6.0;
const GUST_SPEED = 3.0;                  // units/s in the gust field (angular x 10)
const GUST_RADIUS = 3.5;
const GUST_EVERY_S = [8, 20];
const MAX_GUSTS = 3;
const FIELD_SCALE = 10;                  // angular coordinates -> gust field units

// --- light -----------------------------------------------------------------------
const SUN_DIR = [-0.36, 0.30, -1.0];     // behind the canopy, off towards a corner
// The shafts rest at 30% and rise when a gust turns the leaves on the sun's
// path edge-on, then die away once it has passed.
const RAY_REST = 0.30;
const RAY_ATTACK = 5.0, RAY_DECAY = 0.7;   // per second
const SUN_PATH_COS = 0.990;                // leaves within ~8 degrees of the sun
const SAFE_FLOOR = 0.5;

// --- events ----------------------------------------------------------------------
const FLIP_SHARE = 0.02;                 // one leaf in fifty flips on each bar
const DROP_FALL = 30;
const FALL_POOL = 64;
const FALL_SECONDS = 4.5;
const LATE_SEASON = 0.75;                // past this, leaves come down regularly
const LATE_FALL_EVERY_S = 3.5;

const TIER_LAYOUT = {
  0: { leaves: 4000, rays: false, dust: 0, fall: true },
  1: { leaves: 7000, rays: true, dust: 0, fall: true },
  2: { leaves: 12000, rays: true, dust: 320, fall: true }
};

const _v = new THREE.Vector3();
const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function lcg(seed) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}

// --- shaders -------------------------------------------------------------------

const SAFE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAt(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const HASH_CHUNK = `
  float hash11(float p) { p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
  float hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash21(i), hash21(i + vec2(1.0, 0.0)), f.x), mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), f.x), f.y);
  }
`;

// The colours of a leaf, shared by the canopy and the falling ones: the
// season's green-to-gold, the silver back, the transmission.
const LEAF_COLOR_CHUNK = `
  uniform float uHigh;
  vec3 leafGreen(float turn) {
    vec3 summer = vec3(0.30, 0.56, 0.17);
    vec3 autumn = vec3(0.96, 0.70, 0.12);
    return mix(summer, autumn, turn);
  }
  // The back: luminance up, saturation halved - and the highs push it.
  vec3 silver(vec3 c) {
    float l = dot(c, vec3(0.299, 0.587, 0.114));
    return mix(vec3(l), c, 0.5) * vec3(0.94, 1.0, 1.10) * (1.6 + 0.5 * uHigh);
  }
  // FOUR STATES, from two angles and one direction:
  //   front : 1 = the green face towards us (from the pivot)
  //   edgeK : 0 = the blade seen edge-on from the camera, 1 = flat to us
  //   trans : |n . sun| - how much sun passes through the blade
  //   fwd   : how nearly the leaf lies between us and the sun (forward scatter)
  // Facing the sun near its path: translucent gold-green. Edge-on: nearly
  // black. Back to us: pale silver-green. Away from the sun: middle green.
  vec3 leafColor(vec3 green, float front, float edgeK, float trans, float fwd, float sunNear, float var) {
    vec3 gold = mix(vec3(0.60, 0.80, 0.12), vec3(1.0, 0.66, 0.10), step(0.6, green.r));
    float t2 = trans * trans;
    vec3 lit = green * (0.035 + 0.42 * trans) + gold * (t2 * fwd * 0.95);
    vec3 back = silver(green) * (0.20 + 0.24 * trans) + gold * (t2 * fwd * 0.30);
    vec3 c = mix(back, lit, front);
    c *= mix(0.14, 1.0, edgeK);
    // +-6% per leaf, per channel: no two leaves quite the same green.
    vec3 h = fract(vec3(var * 13.1, var * 71.7, var * 37.3)) * 2.0 - 1.0;
    c *= 1.0 + 0.06 * h;
    // Right against the sun's disc the blades go nearly white.
    return mix(c, vec3(1.1, 1.05, 0.92) * (0.30 + 0.70 * trans) * edgeK, sunNear * 0.28);
  }
`;

// A leaf quad: x across (-1..1), y along the petiole (0 at the attachment, 1 at
// the tip). The pivot rotates the blade about the petiole axis a.
const LEAF_SHAPE_CHUNK = `
  // Aspen: nearly round, a short fine point, a slight heart at the base, on a
  // short petiole.
  float leafMask(vec2 uv, out float rib) {
    float y = uv.y;
    rib = 0.0;
    if (y < 0.10) return step(abs(uv.x), 0.06);   // the petiole
    float yy = (y - 0.10) / 0.90;
    float w = 0.98 * pow(sin(3.14159 * clamp(yy, 0.0, 1.0)), 0.62) * (1.0 - 0.30 * smoothstep(0.55, 1.0, yy));
    w *= 1.0 - 0.25 * (1.0 - smoothstep(0.0, 0.12, yy)) * (1.0 - smoothstep(0.0, 0.25, abs(uv.x)));   // the heart
    rib = 1.0 - smoothstep(0.0, 0.05, abs(uv.x));
    return step(abs(uv.x), w);
  }
`;

const leafVertexShader = `
  precision highp float;
  attribute vec3 aPos;
  attribute vec3 aAxis;       // the petiole's direction, unit
  attribute vec3 aNormal0;    // the blade's normal at rest, unit, perpendicular to aAxis
  attribute vec4 aLeaf;       // size, freq, phase, seed
  attribute vec2 aBranch;     // branch index, along (0..1)
  attribute float aId;
  uniform float uTime;
  uniform vec4 uGust[${MAX_GUSTS}];   // field x, y, radius, strength
  uniform vec3 uBranchOff[${MAX_MAINS}];
  uniform float uBaseAmp;     // radians
  uniform float uBar;         // bar index
  uniform float uBarT;        // beats since the bar
  uniform vec3 uSunDir;
  uniform vec2 uSunNdc;
  uniform float uSingle;
  varying vec2 vUv;
  varying vec4 vLight;        // front, edgeK, trans, fwd
  varying vec4 vMisc;         // sunNear, safe, turn, shade
  varying float vVar;
  ${SAFE_CHUNK}
  ${HASH_CHUNK}
  uniform float uSeason;
  void main() {
    vUv = vec2(position.x, position.y);
    float size = aLeaf.x, freq = aLeaf.y, phase = aLeaf.z, seed = aLeaf.w;
    // The gust field: angular position, scaled.
    vec2 q = aPos.xy / max(-aPos.z, 0.1) * ${FIELD_SCALE.toFixed(1)};
    float g = 0.0;
    for (int i = 0; i < ${MAX_GUSTS}; i++) {
      vec2 d = q - uGust[i].xy;
      g += uGust[i].w * exp(-dot(d, d) / max(uGust[i].z * uGust[i].z, 0.01));
    }
    g = clamp(g, 0.0, 1.5);
    // Two oscillators blended, never one whose frequency changes: a
    // changing frequency times a growing t jumps the phase every frame.
    float rest = sin(6.2831853 * freq * uTime + phase);
    float fast = sin(6.2831853 * ${FREQ_GUST.toFixed(1)} * uTime + phase * 1.7 + seed * 5.0);
    float gw = clamp(g, 0.0, 1.0);
    float angle = uBaseAmp * rest * (1.0 - gw) + ${(GUST_AMP_DEG * Math.PI / 180).toFixed(4)} * fast * gw * (0.6 + 0.4 * min(g, 1.5));
    // One leaf in fifty, drawn by lot each bar: a full half-turn and back in a beat.
    if (hash11(aId * 0.618 + uBar * 7.13) < ${FLIP_SHARE.toFixed(3)}) angle += 3.14159 * sin(3.14159 * clamp(uBarT, 0.0, 1.0));
    // The pivot: the blade's side vector turned about the petiole axis.
    vec3 a = aAxis, n0 = aNormal0;
    vec3 b0 = cross(n0, a);
    float c = cos(angle), s = sin(angle);
    vec3 b = b0 * c + n0 * s;
    vec3 n = n0 * c - b0 * s;
    vec3 base = aPos + uBranchOff[int(aBranch.x)] * aBranch.y;
    vec3 world = base + a * position.y * size + b * position.x * size * 0.55;
    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    vec3 toCam = normalize(cameraPosition - world);
    float facing = dot(n, toCam);
    vec3 sunDir = normalize(uSunDir);
    vec2 ndc = clip.xy / max(clip.w, 0.001);
    vec2 ds = (ndc - uSunNdc) * vec2(uAspect, 1.0);
    // The season, patch by patch: each leaf turns at its own moment.
    float h = hash11(seed * 91.7 + floor(q.x * 0.35) * 3.1 + floor(q.y * 0.35) * 7.7);
    float turn = smoothstep(0.12 + h * 0.66, 0.12 + h * 0.66 + 0.22, uSeason);
    float fwd = pow(clamp((dot(-toCam, sunDir) - 0.62) / 0.38, 0.0, 1.0), 2.2);
    vLight = vec4(smoothstep(-0.15, 0.15, facing), smoothstep(0.0, 0.32, abs(facing)), abs(dot(n, sunDir)), fwd);
    // Far leaves take a little of the sky's haze - the depth of the crown.
    vMisc = vec4(exp(-dot(ds, ds) * 5.5), uSingle > 0.5 ? 1.0 : safeAt(clip), turn, clamp(1.0 - (-mv.z - 4.0) / 90.0, 0.80, 1.0));
    vVar = seed;
  }
`;
const leafFragmentShader = `
  precision mediump float;
  varying vec2 vUv;
  varying vec4 vLight;
  varying vec4 vMisc;
  varying float vVar;
  ${LEAF_SHAPE_CHUNK}
  ${LEAF_COLOR_CHUNK}
  void main() {
    float rib;
    if (leafMask(vUv, rib) < 0.5) discard;
    vec3 c = leafColor(leafGreen(vMisc.z), vLight.x, vLight.y, vLight.z, vLight.w, vMisc.x, vVar);
    c *= 1.0 - 0.18 * rib;
    c = mix(vec3(0.14, 0.22, 0.34), c, vMisc.w);
    gl_FragColor = vec4(c * vMisc.y, 1.0);
  }
`;

// The falling leaves: same blade, same colours, on a trajectory from the crown
// towards the camera, tumbling, growing, and out by the bottom of the frame.
const fallVertexShader = `
  precision highp float;
  attribute vec3 aOrigin;
  attribute vec2 aFall;       // start time, seed
  uniform float uNow;
  uniform vec3 uCamUp;
  uniform vec3 uCamRight;
  uniform vec3 uSunDir;
  uniform vec2 uSunNdc;
  varying vec2 vUv;
  varying vec4 vLight;        // front, edgeK, trans, fwd
  varying vec4 vMisc;         // sunNear, safe, turn, shade
  varying float vVar;
  ${SAFE_CHUNK}
  void main() {
    float t = (uNow - aFall.x) / ${FALL_SECONDS.toFixed(1)};
    if (t < 0.0 || t > 1.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vUv = vec2(9.0); vLight = vec4(0.0); vMisc = vec4(0.0, 0.0, 1.0, 1.0); vVar = 0.0; return; }
    float seed = aFall.y;
    // Towards the camera and down the frame, fluttering.
    vec3 target = vec3(0.0, 0.0, -1.0) - uCamUp * 1.9 + uCamRight * (seed - 0.5) * 2.4;
    float e = t * t * (3.0 - 2.0 * t);
    vec3 p = mix(aOrigin, target, e);
    p += (uCamRight * sin(t * 9.0 + seed * 6.0) + uCamUp * cos(t * 7.0 + seed * 4.0)) * 0.35 * (1.0 - t) * (0.3 + 0.7 * e);
    // Tumbling: about a slowly turning axis.
    float spin = t * 6.2831853 * (1.5 + seed * 1.5);
    vec3 a = normalize(vec3(cos(seed * 11.0 + t), sin(seed * 11.0 + t), 0.4));
    vec3 n0 = normalize(cross(a, vec3(0.3, -0.2, 1.0)));
    vec3 b0 = cross(n0, a);
    vec3 b = b0 * cos(spin) + n0 * sin(spin);
    vec3 n = n0 * cos(spin) - b0 * sin(spin);
    float size = ${LEAF_SIZE.toFixed(2)} * 1.1;
    vUv = position.xy;
    vec3 world = p + a * (position.y - 0.5) * size + b * position.x * size * 0.55;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    gl_Position = clip;
    vec3 toCam = normalize(cameraPosition - world);
    float facing = dot(n, toCam);
    vec3 sunDir = normalize(uSunDir);
    float fwd = pow(clamp((dot(-toCam, sunDir) - 0.62) / 0.38, 0.0, 1.0), 2.2);
    vLight = vec4(smoothstep(-0.15, 0.15, facing), smoothstep(0.0, 0.32, abs(facing)), abs(dot(n, sunDir)), fwd);
    vec2 ndc = clip.xy / max(clip.w, 0.001);
    vec2 ds = (ndc - uSunNdc) * vec2(uAspect, 1.0);
    // the ones that come down are the turned ones
    vMisc = vec4(exp(-dot(ds, ds) * 5.5), safeAt(clip), 0.55 + 0.45 * seed, 1.0);
    vVar = seed;
  }
`;

// The sky between the leaves: afternoon blue, the soft sun disc towards a
// corner, lighter around it; the palette tints it a fifth.
const skyVertexShader = `
  precision mediump float;
  varying vec2 vNdc;
  void main() { vNdc = position.xy; gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;
const skyFragmentShader = `
  precision mediump float;
  uniform vec2 uSunNdc;
  uniform float uAspect;
  uniform vec3 uTint;
  uniform float uSeason;
  varying vec2 vNdc;
  void main() {
    vec2 d = (vNdc - uSunNdc) * vec2(uAspect, 1.0);
    float r = length(d);
    // LINEAR, set against the rendered frame (ACES x 1.1): an afternoon blue
    // of 0.42/0.64/0.92 came out as a flat white-grey at 200/255.
    vec3 blue = mix(vec3(0.045, 0.12, 0.34), uTint * 0.35, 0.20);
    vec3 col = mix(blue, vec3(0.30, 0.36, 0.45), exp(-r * 2.2) * 0.9);
    // Autumn light is warmer and lower.
    col = mix(col, col * vec3(1.08, 0.95, 0.78), uSeason * 0.6);
    col += vec3(2.2, 2.0, 1.7) * exp(-r * r * 160.0);       // the disc, soft
    col += vec3(0.30, 0.26, 0.18) * exp(-r * 6.0) * 0.6;      // its glow
    gl_FragColor = vec4(col, 1.0);
  }
`;

// Trunks and branches: dark silhouettes, the bark's faint warm rim from the
// sun behind.
// THE TRUNKS: tapered cylinders, drawn as quads across the trunk and shaded as
// round. Aspen bark is pale grey-white, marked with dark horizontal lenticels,
// and lit on one side by the sun (which is behind the canopy, so from the side
// towards it on screen); the other side is in the blue shade of the sky.
const barkVertexShader = `
  precision highp float;
  attribute float aEdge;       // -1..1 across the trunk
  attribute float aAlong;      // world distance up the trunk
  attribute vec2 aPerp;        // the across direction, world xy, unit
  attribute float aTree;
  uniform vec3 uSunDir;
  varying float vEdge;
  varying float vDepth;
  varying float vAlong;
  varying float vSide;
  varying float vTree;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    vEdge = aEdge;
    vAlong = aAlong;
    vSide = dot(aPerp, normalize(uSunDir.xy));
    vTree = aTree;
    vDepth = clamp((-mv.z - 2.0) / 40.0, 0.0, 1.0);
  }
`;
const barkFragmentShader = `
  precision mediump float;
  uniform float uDebug;
  varying float vEdge;
  varying float vDepth;
  varying float vAlong;
  varying float vSide;
  varying float vTree;
  ${HASH_CHUNK}
  void main() {
    if (uDebug > 0.5) {
      float k = floor(vTree + 0.5);
      vec3 d = k < 0.5 ? vec3(1.0, 0.23, 0.19) : k < 1.5 ? vec3(1.0, 0.8, 0.0) : k < 2.5 ? vec3(0.2, 0.78, 0.35) : k < 3.5 ? vec3(0.0, 0.78, 1.0) : k < 4.5 ? vec3(0.35, 0.34, 0.84) : vec3(1.0, 0.18, 1.0);
      gl_FragColor = vec4(d * 0.6, 1.0);
      return;
    }
    // Round: the angle around the cylinder from the quad's across coordinate.
    float e = clamp(vEdge, -0.999, 0.999);
    float ang = asin(e);
    // The sun's side: lambert on the side facing it, wrapped a little.
    float key = clamp(e * sign(vSide) * 0.85 + 0.25, 0.0, 1.0);
    key *= key * (0.35 + 0.65 * abs(vSide));
    float round = sqrt(1.0 - e * e);
    vec3 bark = vec3(0.60, 0.60, 0.56);
    vec3 c = bark * (vec3(0.05, 0.065, 0.09) * (0.5 + 0.5 * round) + vec3(0.62, 0.58, 0.48) * key);
    // THE LENTICELS: short dark horizontal marks, rows up the trunk, each one
    // around part of the girth. Row index stays small (trunks are ~35 units).
    // Two marks per row, at different places around the girth, each of its
    // own length and thickness, off the row's centre line - never a ruler.
    float rowLen = 0.34;
    float row = floor(vAlong / rowLen);
    float y = fract(vAlong / rowLen);
    float mark = 0.0;
    for (int m = 0; m < 2; m++) {
      float fm = float(m);
      float h1 = hash11(row * 1.37 + fm * 11.3 + 0.5), h2 = hash11(row * 2.71 + fm * 5.9 + 7.1), h3 = hash11(row * 5.13 + fm * 3.7 + 3.3);
      float centre = (h1 - 0.5) * 2.6;
      float halfW = 0.12 + 0.70 * h2 * h2;
      float thick = 0.06 + 0.16 * h3 * h2;
      float yc = 0.25 + 0.5 * hash11(row * 3.91 + fm * 1.7);
      // a lenticel is fatter in its middle and pointed at its ends
      float da = abs(ang - centre) / halfW;
      float th = thick * sqrt(max(0.0, 1.0 - da * da));
      float mk = 1.0 - smoothstep(th * 0.55, th, abs(y - yc));
      mark = max(mark, mk * step(da, 1.0) * step(0.3, hash11(row * 7.7 + fm * 2.3)));
    }
    c = mix(c, vec3(0.012, 0.011, 0.010), mark * 0.92);
    // far up, into the haze of the sky
    c = mix(c, vec3(0.20, 0.28, 0.40), vDepth * 0.5);
    gl_FragColor = vec4(c, 1.0);
  }
`;

// The shafts: from the sun through the canopy when a gust opens it.
const rayVertexShader = skyVertexShader;
const rayFragmentShader = `
  precision mediump float;
  uniform vec2 uSunNdc;
  uniform float uAspect;
  uniform float uRay;
  uniform float uTime;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vNdc;
  ${HASH_CHUNK}
  void main() {
    vec2 d = (vNdc - uSunNdc) * vec2(uAspect, 1.0);
    float r = length(d);
    float a = atan(d.y, d.x);
    float streak = pow(vnoise(vec2(a * 14.0, uTime * 0.15)), 3.0) * 1.6;
    float fall = exp(-r * 0.9) * smoothstep(0.02, 0.2, r);
    vec2 s = vec2(vNdc.x * uAspect, vNdc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float safe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(vec3(1.0, 0.94, 0.78) * streak * fall * uRay * 0.20 * safe, 1.0);
  }
`;

// Dust and poplar seeds adrift in the shafts.
const dustVertexShader = `
  precision mediump float;
  attribute float aSeed;
  uniform float uTime;
  uniform float uPx;
  uniform float uRay;
  varying float vB;
  void main() {
    vec3 p = position;
    p.x += sin(uTime * 0.21 + aSeed * 40.0) * 0.35;
    p.y += sin(uTime * 0.17 + aSeed * 23.0) * 0.30 + uTime * 0.03 * (aSeed - 0.3);
    p.z += cos(uTime * 0.13 + aSeed * 11.0) * 0.25;
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = clamp((aSeed > 0.85 ? 0.10 : 0.035) * uPx / max(-mv.z, 0.1), 1.0, 9.0);
    vB = (0.06 + 0.9 * uRay) * (0.4 + 0.6 * aSeed);
  }
`;
const dustFragmentShader = `
  precision mediump float;
  varying float vB;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float a = 1.0 - smoothstep(0.1, 0.5, length(d));
    gl_FragColor = vec4(vec3(1.0, 0.96, 0.85) * a * vB, 1.0);
  }
`;

export class AspenVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER_LAYOUT[this._tier] || TIER_LAYOUT[1];
    this._debugGust = urlParam('aspengust') === '1';
    const season = parseFloat(urlParam('aspenseason'));
    this._forceSeason = Number.isFinite(season) ? season : null;
    this._single = urlParam('aspensingle') === '1';
    this._debugBranches = urlParam('aspenbranches') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._time = 0;
    this._beatSeconds = 0.6;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._freeBeatAt = 0; this._beatCount = 0; this._bar = 0; this._barAt = 0;
    this._pendingDropMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._nextGustAt = 3; this._debugGustAt = 1.5;
    this._lastLateFall = 0;
    this._rnd = lcg(0xa5be);
    this._gusts = [];
    this._objects = []; this._materials = []; this._geometries = [];
    this._sunDir = new THREE.Vector3(SUN_DIR[0], SUN_DIR[1], SUN_DIR[2]).normalize();
    this._tint = new THREE.Color(0.42, 0.64, 0.92);
    this._shared = {
      uTime: { value: 0 },
      uSunDir: { value: this._sunDir },
      uSunNdc: { value: new THREE.Vector2(-0.5, 0.4) },
      uSeason: { value: 0 },
      uHigh: { value: 0 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._branchOff = [];
    for (let i = 0; i < MAX_MAINS; i++) this._branchOff.push(new THREE.Vector3());
    this._branchVel = this._branchOff.map(() => new THREE.Vector3());
    this._placeCamera();
    this._buildSky();
    this._buildTree();
    this._buildLeaves();
    if (this._layout.fall) this._buildFall();
    if (this._layout.rays && !this._single) this._buildRays();
    if (this._layout.dust && !this._single) this._buildDust();
    this._applyPalette(null);
  }

  _placeCamera() {
    const c = this._camera;
    const a = this._single ? 0 : (this._time / CAMERA_TURN_S) * Math.PI * 2;
    c.position.set(0, 0, 0);
    c.up.set(Math.sin(a), Math.cos(a), 0);
    c.lookAt(0, 0, -1);
    c.updateMatrixWorld();
  }

  _uniforms(extra) {
    const u = {};
    for (const k of Object.keys(this._shared)) u[k] = this._shared[k];
    return Object.assign(u, extra || {});
  }

  _add(mesh, geo, mat, order) {
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    this._objects.push(mesh);
    if (geo) this._geometries.push(geo);
    if (mat) this._materials.push(mat);
    this._scene.add(mesh);
    return mesh;
  }

  _buildSky() {
    const geo = new THREE.PlaneGeometry(2, 2);
    this._skyUniforms = this._uniforms({ uTint: { value: this._tint } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._skyUniforms, vertexShader: skyVertexShader, fragmentShader: skyFragmentShader, depthTest: false, depthWrite: false });
    const mesh = this._add(new THREE.Mesh(geo, mat), geo, mat, -1000);
    const self = this;
    mesh.onBeforeRender = function (renderer) {
      const size = renderer.getDrawingBufferSize(new THREE.Vector2());
      self._px = (size.y / 2) / Math.tan(THREE.MathUtils.degToRad(self._camera.fov) / 2);
      if (self._dustUniforms) self._dustUniforms.uPx.value = self._px;
      for (const l of self._lines || []) l.mat.resolution.set(size.x, size.y);
    };
  }

  // THE TREE. TRUNKS trunks from the frame's rim near the camera, converging on
  // (but not reaching) the far centre. Off each trunk 3-5 limbs, off each limb
  // 3-4 branches, off each branch 4-6 twigs, every fork turning 20-50 degrees
  // and shortening. Trunks are tapered quads; limbs, branches and twigs are
  // dark fat lines of 3.5 / 2 / 1.2 px. Every point of a limb's subtree records
  // its limb and how far out it sits, which is how much of the limb's sway it
  // takes (uBranchOff in the leaves, a CPU update for the lines).
  _buildTree() {
    const r = this._rnd;
    const tanH = Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2);
    const tanW = tanH * Math.max(this._camera.aspect || 16 / 9, 16 / 9);
    const pos = [], edge = [], along = [], perp = [], tree = [], idx = [];
    // one piece of trunk: a quad across it, facing the camera's axis
    const quad = (p0, p1, w0, w1, a0, a1, t) => {
      const dx = p1.x - p0.x, dy = p1.y - p0.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = -dy / len, ny = dx / len;
      const base = pos.length / 3;
      for (const [p, w, e, al] of [[p0, w0, -1, a0], [p0, w0, 1, a0], [p1, w1, 1, a1], [p1, w1, -1, a1]]) {
        pos.push(p.x + nx * w * e, p.y + ny * w * e, p.z); edge.push(e); along.push(al); perp.push(nx, ny); tree.push(t);
      }
      idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    };
    const between = (lo, hi) => lo + Math.floor(r() * (hi - lo + 1));
    // Turn dir by lo-hi degrees towards a random side (pulled by bias), never
    // back towards the camera.
    const fork = (dir, lo, hi, bias) => {
      const side = new THREE.Vector3(r() - 0.5, r() - 0.5, r() - 0.5);
      if (bias) side.addScaledVector(bias, 2.0);
      side.addScaledVector(dir, -side.dot(dir)).normalize();
      const th = THREE.MathUtils.degToRad(lo + r() * (hi - lo));
      const d = dir.clone().multiplyScalar(Math.cos(th)).addScaledVector(side, Math.sin(th));
      if (d.z > -0.15) { d.z = -0.15; }
      return d.normalize();
    };
    this._lineData = LINE_PX.map(() => ({ pos: [], main: [], w: [], col: [] }));
    const haze = new THREE.Color(0.20, 0.28, 0.40), dark = new THREE.Color(0.030, 0.030, 0.027);
    const debugCol = new THREE.Color();
    const seg = (cls, a, b, main) => {
      const L = this._lineData[cls];
      for (const p of [a, b]) {
        L.pos.push(p.x, p.y, p.z);
        L.main.push(main.id);
        L.w.push(Math.min(1.3, p.distanceTo(main.s0) / main.len));
        if (this._debugBranches) _col.copy(debugCol.set(DEBUG_TREE_COLORS[main.trunk % DEBUG_TREE_COLORS.length]));
        else _col.copy(dark).lerp(haze, THREE.MathUtils.clamp((-p.z - 2) / 40, 0, 1) * 0.55);
        L.col.push(_col.r, _col.g, _col.b);
      }
    };
    // A LIMB, BRANCH OR TWIG: a curve of 3 pieces bowed off the straight line,
    // starting EXACTLY at a (a point of its parent, in world coordinates like
    // everything here). at(v) gives the point a child starts from.
    const curve = (a, D, len, classes, main) => {
      const bow = new THREE.Vector3(r() - 0.5, r() - 0.5, r() - 0.5);
      bow.addScaledVector(D, -bow.dot(D)).normalize().multiplyScalar(len * (0.05 + 0.06 * r()));
      const pts = [a.clone()];
      for (let k = 1; k <= 3; k++) {
        pts.push(a.clone().addScaledVector(D, len * k / 3).addScaledVector(bow, Math.sin(Math.PI * k / 3) * (k === 3 ? 0.4 : 1)));
      }
      for (let k = 0; k < 3; k++) seg(classes[k], pts[k], pts[k + 1], main);
      const at = (v) => {
        const f = THREE.MathUtils.clamp(v, 0, 1) * 3, k = Math.min(2, Math.floor(f));
        return new THREE.Vector3().lerpVectors(pts[k], pts[k + 1], f - k);
      };
      const dirAt = (v) => { const k = Math.min(2, Math.floor(THREE.MathUtils.clamp(v, 0, 1) * 3)); return pts[k + 1].clone().sub(pts[k]).normalize(); };
      return { at, dirAt, end: pts[3] };
    };
    this._branches = [];    // the limbs (they sway)
    this._twigs = [];
    let mainId = 0;
    for (let t = 0; t < TRUNKS; t++) {
      const ang = (t / TRUNKS) * Math.PI * 2 + (r() - 0.5) * 0.5;
      const rimR = 1.15 + r() * 0.25;
      const nearZ = NEAR_Z - r() * 0.8;
      const p0 = new THREE.Vector3(Math.cos(ang) * tanW * rimR * -nearZ, Math.sin(ang) * tanH * rimR * -nearZ * 1.4, nearZ);
      const topZ = FAR_Z + r() * 6;
      const topAng = ang + (r() - 0.5) * 0.3;
      const p1 = new THREE.Vector3(Math.cos(topAng) * TRUNK_END_ANGULAR * -topZ * 1.3, Math.sin(topAng) * TRUNK_END_ANGULAR * -topZ, topZ);
      // a cylinder tapering from 0.30 to 0.07 in radius
      const w0 = 0.30, w1 = 0.07;
      const segs = 10;
      let prev = p0.clone(), prevAlong = 0;
      for (let k = 1; k <= segs; k++) {
        const u = k / segs;
        const p = new THREE.Vector3().lerpVectors(p0, p1, u);
        p.x += Math.sin(u * 5 + t) * 0.08 * -p.z * 0.02;
        const al = prevAlong + p.distanceTo(prev);
        quad(prev, p, w0 + (w1 - w0) * ((k - 1) / segs), w0 + (w1 - w0) * u, prevAlong, al, t);
        prev = p; prevAlong = al;
      }
      const T = p1.clone().sub(p0);
      const trunkLen = T.length();
      T.normalize();
      const nMain = Math.min(between(MAINS_PER_TRUNK[0], MAINS_PER_TRUNK[1]), MAX_MAINS - mainId);
      for (let m = 0; m < nMain; m++) {
        // low limbs near the camera at the rim, high ones far towards the centre
        const u = 0.05 + 0.90 * (m + 0.2 + r() * 0.6) / nMain;
        const s0 = new THREE.Vector3().lerpVectors(p0, p1, u);
        s0.x += Math.sin(u * 5 + t) * 0.08 * -s0.z * 0.02;   // on the trunk's own bend
        // Limbs spread SIDEWAYS from the trunk (seen from below: along the
        // rim for the low ones); only the top one leans in over the centre.
        const inward = new THREE.Vector3(-s0.x, -s0.y, 0).normalize();
        const tangent = new THREE.Vector3(-inward.y, inward.x, 0).multiplyScalar(r() < 0.5 ? 1 : -1);
        const top = m === nMain - 1;
        const lean = top ? inward : tangent.addScaledVector(inward, u < 0.4 ? 0.3 : 0.9).normalize();
        const D = fork(T, top ? 45 : 45, top ? 60 : 65, lean);
        // the top limb reaches right over the centre, the others stay shorter
        const len = trunkLen * (0.17 + 0.08 * r()) * (1 - 0.30 * u) * (top ? 1.5 : 1);
        const main = { id: mainId++, s0, len, trunk: t, height: u };
        const limb = curve(s0, D, len, LIMB_CLASSES, main);
        main.s1 = limb.end;
        this._branches.push(main);
        const nSub = between(SUBS_PER_MAIN[0], SUBS_PER_MAIN[1]);
        for (let b = 0; b < nSub; b++) {
          const v = 0.35 + 0.65 * (b + r() * 0.8) / nSub;
          const b0 = limb.at(v);
          const SD = fork(limb.dirAt(v), 20, 50, null);
          const sLen = len * (0.45 + 0.15 * r());
          const br = curve(b0, SD, sLen, BRANCH_CLASSES, main);
          const nTwig = between(TWIGS_PER_SUB[0], TWIGS_PER_SUB[1]);
          for (let k = 0; k < nTwig; k++) {
            const w = 0.30 + 0.70 * (k + r() * 0.8) / nTwig;
            const t0 = br.at(w);
            const TD = fork(br.dirAt(w), 20, 50, null);
            const tw = curve(t0, TD, sLen * (0.35 + 0.15 * r()), TWIG_CLASSES, main);
            this._twigs.push({ a: t0, b: tw.end, main, height: u });
          }
        }
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
    geo.setAttribute('aEdge', new THREE.BufferAttribute(new Float32Array(edge), 1));
    geo.setAttribute('aAlong', new THREE.BufferAttribute(new Float32Array(along), 1));
    geo.setAttribute('aPerp', new THREE.BufferAttribute(new Float32Array(perp), 2));
    geo.setAttribute('aTree', new THREE.BufferAttribute(new Float32Array(tree), 1));
    geo.setIndex(idx);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uSunDir: this._shared.uSunDir, uDebug: { value: this._debugBranches ? 1 : 0 } },
      vertexShader: barkVertexShader, fragmentShader: barkFragmentShader, side: THREE.DoubleSide
    });
    this._bark = this._add(new THREE.Mesh(geo, mat), geo, mat, -500);
    if (this._single) this._bark.visible = false;
    // the limbs, branches and twigs: independent segments (pairs), one mesh per width
    this._lines = [];
    for (let o = 0; o < LINE_PX.length; o++) {
      const L = this._lineData[o];
      if (!L.pos.length) continue;
      const lg = new LineSegmentsGeometry();
      lg.setPositions(L.pos);
      lg.setColors(L.col);
      const lm = new LineMaterial({ linewidth: LINE_PX[o], vertexColors: true, alphaToCoverage: false });
      lm.resolution.set(1280, 720);
      const mesh = this._add(new LineSegments2(lg, lm), lg, lm, -480 + o);
      if (this._single) mesh.visible = false;
      this._lines.push({ geo: lg, mat: lm, base: new Float32Array(L.pos), main: L.main, w: L.w });
    }
    this._lineData = null;
  }

  // Sway the lines with their limbs.
  _updateLines() {
    for (const l of this._lines || []) {
      const arr = l.geo.attributes.instanceStart.data.array;
      const base = l.base, main = l.main, w = l.w;
      for (let v = 0; v < main.length; v++) {
        const o = this._branchOff[main[v]], k = w[v];
        arr[v * 3] = base[v * 3] + o.x * k;
        arr[v * 3 + 1] = base[v * 3 + 1] + o.y * k;
        arr[v * 3 + 2] = base[v * 3 + 2] + o.z * k;
      }
      l.geo.attributes.instanceStart.data.needsUpdate = true;
    }
  }

  _buildLeaves() {
    const r = this._rnd;
    // Leaves per twig: 15-40 at the richest tier, fewer below it.
    const twigs = this._twigs;
    const full = twigs.length * (LEAVES_PER_TWIG[0] + LEAVES_PER_TWIG[1]) / 2;
    const scale = Math.min(1, this._layout.leaves / full);
    const counts = twigs.map(() => Math.max(3, Math.round((LEAVES_PER_TWIG[0] + r() * (LEAVES_PER_TWIG[1] - LEAVES_PER_TWIG[0])) * scale)));
    const n = this._single ? 1 : counts.reduce((a, b) => a + b, 0);
    const quad = new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]);
    const geo = new THREE.InstancedBufferGeometry();
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    const P = new Float32Array(n * 3), AX = new Float32Array(n * 3), N0 = new Float32Array(n * 3), L = new Float32Array(n * 4), B = new Float32Array(n * 2), ID = new Float32Array(n);
    const axis = new THREE.Vector3(), nrm = new THREE.Vector3(), tmp = new THREE.Vector3();
    const gauss = () => { const u = Math.max(1e-6, r()), v = r(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v); };
    let twig = 0, left = counts[0];
    for (let i = 0; i < n; i++) {
      let p;
      let bIndex = 0, al = 1;
      let leafSize = LEAF_SIZE;
      if (this._single) {
        // In the lower right, away from the sun's disc, which whitens
        // everything near it and would hide both faces' colours.
        p = new THREE.Vector3(0.55, -0.55, -2.4);
        al = 0;    // no limb sway: the leaf stays put for the camera
      } else {
        // IN A BUNCH ON A TWIG: along it (more towards its tip), with a
        // gaussian spread of LEAF_SPREAD around it.
        while (left <= 0) { twig++; left = counts[twig]; }
        left--;
        const tw = twigs[twig];
        const s = 0.15 + 0.85 * Math.sqrt(r());
        p = new THREE.Vector3().lerpVectors(tw.a, tw.b, s);
        p.x += gauss() * LEAF_SPREAD; p.y += gauss() * LEAF_SPREAD; p.z += gauss() * LEAF_SPREAD;
        if (p.z > -1.6) p.z = -1.6;
        leafSize = LEAF_SIZE * (LEAF_SIZE_LOW + (LEAF_SIZE_HIGH - LEAF_SIZE_LOW) * tw.height) * (0.8 + 0.4 * r());
        bIndex = tw.main.id;
        al = Math.min(1.3, p.distanceTo(tw.main.s0) / tw.main.len);
      }
      P[i * 3] = p.x; P[i * 3 + 1] = p.y; P[i * 3 + 2] = p.z;
      // HOW A LEAF HANGS. Facing the camera flat, no pivot short of 90 degrees
      // ever shows its back. An aspen leaf hangs at any angle: tilted 15 to 120
      // degrees off our line of sight, turning about the axis that tips it
      // towards and away from us. Past 90 its back is already ours at rest;
      // near 90 it is a dark sliver; a gust swings them all through.
      const toward = tmp.copy(p).multiplyScalar(-1).normalize();
      const side = new THREE.Vector3(r() - 0.5, r() - 0.5, r() - 0.5).cross(toward).normalize();
      const tilt = this._single ? 55 * Math.PI / 180 : (15 + 105 * r()) * Math.PI / 180;
      nrm.copy(toward).multiplyScalar(Math.cos(tilt)).addScaledVector(side, Math.sin(tilt)).normalize();
      axis.copy(nrm).cross(toward).normalize();
      if (!this._single) {
        // a little of the petiole's own lie, still in the blade's plane
        const jitter = new THREE.Vector3(r() - 0.5, r() - 0.5, r() - 0.5).multiplyScalar(0.5);
        axis.add(jitter).addScaledVector(nrm, -axis.dot(nrm)).normalize();
      }
      AX[i * 3] = axis.x; AX[i * 3 + 1] = axis.y; AX[i * 3 + 2] = axis.z;
      N0[i * 3] = nrm.x; N0[i * 3 + 1] = nrm.y; N0[i * 3 + 2] = nrm.z;
      const size = this._single ? 1.1 : leafSize;
      L[i * 4] = size;
      L[i * 4 + 1] = FREQ_REST[0] + r() * (FREQ_REST[1] - FREQ_REST[0]);
      L[i * 4 + 2] = r() * Math.PI * 2;
      L[i * 4 + 3] = r();
      B[i * 2] = bIndex; B[i * 2 + 1] = al;
      ID[i] = i;
    }
    geo.setAttribute('aPos', new THREE.InstancedBufferAttribute(P, 3));
    geo.setAttribute('aAxis', new THREE.InstancedBufferAttribute(AX, 3));
    geo.setAttribute('aNormal0', new THREE.InstancedBufferAttribute(N0, 3));
    geo.setAttribute('aLeaf', new THREE.InstancedBufferAttribute(L, 4));
    geo.setAttribute('aBranch', new THREE.InstancedBufferAttribute(B, 2));
    geo.setAttribute('aId', new THREE.InstancedBufferAttribute(ID, 1));
    geo.instanceCount = n;
    this._leafPositions = P;
    // The leaves on the sun's path: their gust state opens the shafts.
    this._sunLeaves = [];
    for (let i = 0; i < n && !this._single; i++) {
      tmp.set(P[i * 3], P[i * 3 + 1], P[i * 3 + 2]).normalize();
      if (tmp.dot(this._sunDir) > SUN_PATH_COS) this._sunLeaves.push(i);
    }
    if (this._sunLeaves.length > 400) this._sunLeaves = this._sunLeaves.filter((_, k) => k % Math.ceil(this._sunLeaves.length / 400) === 0);
    const gusts = [];
    for (let i = 0; i < MAX_GUSTS; i++) gusts.push(new THREE.Vector4(0, 0, 1, 0));
    this._leafUniforms = this._uniforms({
      uGust: { value: gusts }, uBranchOff: { value: this._branchOff }, uBaseAmp: { value: BASE_AMP_DEG * Math.PI / 180 },
      uBar: { value: 0 }, uBarT: { value: 9 }, uSingle: { value: this._single ? 1 : 0 }
    });
    const mat = new THREE.ShaderMaterial({ uniforms: this._leafUniforms, vertexShader: leafVertexShader, fragmentShader: leafFragmentShader, side: THREE.DoubleSide });
    this._leaves = this._add(new THREE.Mesh(geo, mat), geo, mat, -400);
  }

  _buildFall() {
    const quad = new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]);
    const geo = new THREE.InstancedBufferGeometry();
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    geo.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    this._fallOrigin = new Float32Array(FALL_POOL * 3);
    this._fallData = new Float32Array(FALL_POOL * 2);
    for (let i = 0; i < FALL_POOL; i++) this._fallData[i * 2] = -1e6;
    geo.setAttribute('aOrigin', new THREE.InstancedBufferAttribute(this._fallOrigin, 3));
    geo.setAttribute('aFall', new THREE.InstancedBufferAttribute(this._fallData, 2));
    geo.instanceCount = FALL_POOL;
    this._fallGeo = geo;
    this._fallTurn = 0;
    this._fallUniforms = this._uniforms({ uNow: { value: 0 }, uCamUp: { value: new THREE.Vector3(0, 1, 0) }, uCamRight: { value: new THREE.Vector3(1, 0, 0) } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._fallUniforms, vertexShader: fallVertexShader, fragmentShader: leafFragmentShader, side: THREE.DoubleSide });
    this._fall = this._add(new THREE.Mesh(geo, mat), geo, mat, -300);
  }

  // Detach k leaves: from the crown, spread over the next second or so.
  _dropLeaves(k, t) {
    if (!this._fallGeo) return;
    const r = this._rnd, P = this._leafPositions, n = P.length / 3;
    for (let j = 0; j < k; j++) {
      const i = this._fallTurn++ % FALL_POOL;
      const src = Math.floor(r() * n);
      // from somewhere mid-crown, not the very far top
      const z = Math.max(P[src * 3 + 2], -16);
      const sc = z / P[src * 3 + 2];
      this._fallOrigin[i * 3] = P[src * 3] * sc; this._fallOrigin[i * 3 + 1] = P[src * 3 + 1] * sc; this._fallOrigin[i * 3 + 2] = z;
      this._fallData[i * 2] = t + r() * 1.2; this._fallData[i * 2 + 1] = r();
    }
    this._fallGeo.attributes.aOrigin.needsUpdate = true;
    this._fallGeo.attributes.aFall.needsUpdate = true;
  }

  _buildRays() {
    const geo = new THREE.PlaneGeometry(2, 2);
    this._rayUniforms = this._uniforms({ uRay: { value: 0 } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._rayUniforms, vertexShader: rayVertexShader, fragmentShader: rayFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false });
    this._rays = this._add(new THREE.Mesh(geo, mat), geo, mat, 100);
  }

  _buildDust() {
    const n = this._layout.dust, r = this._rnd;
    const pos = new Float32Array(n * 3), seed = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const z = -1.5 - r() * 7;
      pos[i * 3] = (r() * 2 - 1) * -z * 1.1; pos[i * 3 + 1] = (r() * 2 - 1) * -z * 0.7; pos[i * 3 + 2] = z;
      seed[i] = r();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    this._dustUniforms = this._uniforms({ uPx: { value: 500 }, uRay: { value: 0 } });
    const mat = new THREE.ShaderMaterial({ uniforms: this._dustUniforms, vertexShader: dustVertexShader, fragmentShader: dustFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false });
    this._add(new THREE.Points(geo, mat), geo, mat, 90);
  }

  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const src = vivid.length ? vivid[0] : (dominant.length ? dominant[0] : null);
    if (src) { _col.set(src); _col.getHSL(_hsl); this._tint.setHSL(_hsl.h, 0.6, 0.62); } else this._tint.setRGB(0.42, 0.64, 0.92);
  }

  onTrackChange(profile) { this._applyPalette(profile); }

  // A gust: a travelling gaussian crossing the field, from one side to the other.
  _spawnGust(strength, speed, radius) {
    const r = this._rnd;
    const ang = r() * Math.PI * 2;
    const dir = new THREE.Vector2(Math.cos(ang), Math.sin(ang));
    const reach = 14;
    const g = { pos: dir.clone().multiplyScalar(-reach).add(new THREE.Vector2((r() - 0.5) * 6, (r() - 0.5) * 4)), dir, speed, radius, strength, life: (2 * reach) / speed + 1.5, age: 0 };
    if (this._gusts.length >= MAX_GUSTS) this._gusts.shift();
    this._gusts.push(g);
  }

  update(deltaTime, profile) {
    const director = profile.director || {};
    const paused = profile.playbackState !== 'PLAYING';
    if (profile.artworkPalette && profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile); }
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 180) : 0;
    this._beatSeconds = bpm ? 60 / bpm : 0.75;
    const beat = this._beatSeconds;
    const measured = !!director.hasReliableGrid;
    const bands = director.bands || { low: 0, mid: 0, high: 0 };
    const low = measured ? (bands.low || 0) : 0.5, mid = measured ? (bands.mid || 0) : 0.5, high = measured ? (bands.high || 0) : 0.5;
    const progress = profile.progress || 0;
    const season = this._forceSeason != null ? this._forceSeason : progress;

    if (!paused) {
      const dt = Math.min(deltaTime, 0.05);
      this._time += dt;
      const t = this._time;
      this._placeCamera();

      // --- the clocks
      let beatEdge = false, barEdge = false, phraseEdge = false;
      if (profile.beatPhase != null) {
        if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
        this._lastBeatPhase = profile.beatPhase;
      } else if (t - this._freeBeatAt >= beat) { this._freeBeatAt = t; beatEdge = true; }
      if (beatEdge) this._beatCount++;
      if (profile.barPhase != null) {
        if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
        this._lastBarPhase = profile.barPhase;
      } else if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
      if (barEdge) { this._bar++; this._barAt = t; }
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
      this._lastPhrase = phrase;

      // --- the drop: a strong gust across the whole canopy in a beat, then leaves fall
      const ev = director.nextEvent;
      let dropNow = false;
      if (ev && ev.type === 'drop' && ev.msUntil != null) {
        if (ev.msUntil <= dt * 500 + 30) dropNow = true;
        if (this._pendingDropMs != null && this._pendingDropMs < 400 && ev.msUntil > this._pendingDropMs + 250) dropNow = true;
        this._pendingDropMs = ev.msUntil;
      } else {
        if (this._pendingDropMs != null && this._pendingDropMs < 400) dropNow = true;
        this._pendingDropMs = null;
      }
      if (measured && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
      this._lastMode = director.mode;
      if (dropNow && t - this._lastDropAt > 4 * beat) {
        this._lastDropAt = t;
        this._spawnGust(1.5, 28 / beat, 6.0);
        this._dropLeaves(DROP_FALL, t + beat * 0.5);
      }

      // --- gusts: on the phrases, or every 8-20 s
      if (phraseEdge || t >= this._nextGustAt || (this._debugGust && t >= this._debugGustAt)) {
        this._spawnGust(0.7 + 0.6 * low, GUST_SPEED, GUST_RADIUS);
        this._nextGustAt = t + GUST_EVERY_S[0] + this._rnd() * (GUST_EVERY_S[1] - GUST_EVERY_S[0]);
        if (this._debugGust) this._debugGustAt = t + 6;
      }
      for (const g of this._gusts) { g.age += dt; g.pos.addScaledVector(g.dir, g.speed * dt); }
      this._gusts = this._gusts.filter((g) => g.age < g.life);

      // --- late season: leaves come down on their own
      if (season > LATE_SEASON && t - this._lastLateFall > LATE_FALL_EVERY_S * (1.6 - (season - LATE_SEASON) * 2)) {
        this._lastLateFall = t;
        this._dropLeaves(2 + Math.floor(this._rnd() * 3), t);
      }

      // --- the branches: a lagging spring pushed by the gusts over them
      // (Cascade's delay - the limb follows the gust late and swings back)
      for (const br of this._branches || []) {
        const mid = _v.lerpVectors(br.s0, br.s1, 0.7);
        const qx = mid.x / -mid.z * FIELD_SCALE, qy = mid.y / -mid.z * FIELD_SCALE;
        let fx = 0, fy = 0;
        for (const g of this._gusts) {
          const dx = qx - g.pos.x, dy = qy - g.pos.y;
          const w = g.strength * Math.exp(-(dx * dx + dy * dy) / (g.radius * g.radius));
          fx += g.dir.x * w; fy += g.dir.y * w;
        }
        const off = this._branchOff[br.id], vel = this._branchVel[br.id];
        const k = 3.2, damp = 1.4, push = 0.22 * (-mid.z) * 0.05;
        vel.x += (fx * push - k * off.x) * dt - damp * vel.x * dt;
        vel.y += (fy * push - k * off.y) * dt - damp * vel.y * dt;
        off.x += vel.x * dt; off.y += vel.y * dt;
      }
      this._updateLines();
    }

    // --- uniforms
    const s = this._shared;
    s.uTime.value = this._time;
    s.uSeason.value = season;
    s.uHigh.value = high;
    // ?aspensingle=1 shows the pivot at +-70 degrees so both faces come round;
    // the canopy's own rest amplitude is the brief's +-8.
    this._leafUniforms.uBaseAmp.value = this._single ? 70 * Math.PI / 180 : BASE_AMP_DEG * Math.PI / 180 * (0.6 + 0.8 * mid);
    this._leafUniforms.uBar.value = this._bar;
    this._leafUniforms.uBarT.value = (this._time - this._barAt) / beat;
    const gu = this._leafUniforms.uGust.value;
    for (let i = 0; i < MAX_GUSTS; i++) {
      const g = this._gusts[i];
      if (g) {
        // fade in and out at the ends of its crossing
        const env = Math.min(1, g.age / 0.8) * Math.min(1, (g.life - g.age) / 1.2);
        gu[i].set(g.pos.x, g.pos.y, g.radius, g.strength * env);
      } else gu[i].set(0, 0, 1, 0);
    }
    // the sun on screen
    _v.copy(this._sunDir).multiplyScalar(50).project(this._camera);
    s.uSunNdc.value.set(_v.x, _v.y);
    // THE SHAFTS: the share of the leaves on the sun's path that a gust has
    // turned edge-on. At rest 30%; they rise fast when a gust opens the mass in
    // front of the sun and die away slowly once it has passed.
    if (this._rayUniforms || this._dustUniforms) {
      const P = this._leafPositions, list = this._sunLeaves || [];
      let open = 0;
      for (const i of list) {
        const z = -P[i * 3 + 2];
        const qx = P[i * 3] / z * FIELD_SCALE, qy = P[i * 3 + 1] / z * FIELD_SCALE;
        let g = 0;
        for (let k = 0; k < MAX_GUSTS; k++) {
          const w = gu[k].w; if (w <= 0) continue;
          const dx = qx - gu[k].x, dy = qy - gu[k].y;
          g += w * Math.exp(-(dx * dx + dy * dy) / Math.max(gu[k].z * gu[k].z, 0.01));
        }
        open += Math.min(1, g);
      }
      open = list.length ? open / list.length : 0;
      this._open = open;
      const target = RAY_REST + (1 - RAY_REST) * THREE.MathUtils.smoothstep(open, 0.05, 0.45);
      const cur = this._ray != null ? this._ray : RAY_REST;
      const rate = target > cur ? RAY_ATTACK : RAY_DECAY;
      this._ray = cur + (target - cur) * Math.min(1, deltaTime * rate);
      if (this._rayUniforms) this._rayUniforms.uRay.value = this._ray;
      if (this._dustUniforms) this._dustUniforms.uRay.value = (this._ray - RAY_REST) / (1 - RAY_REST);
    }
    if (this._fallUniforms) {
      this._fallUniforms.uNow.value = this._time;
      const e = this._camera.matrixWorld.elements;
      this._fallUniforms.uCamRight.value.set(e[0], e[1], e[2]);
      this._fallUniforms.uCamUp.value.set(e[4], e[5], e[6]);
    }
  }

  dispose() {
    for (const o of this._objects) if (o.parent) o.parent.remove(o);
    for (const g of this._geometries) g.dispose();
    for (const m of this._materials) m.dispose();
    this._objects = []; this._geometries = []; this._materials = [];
    if (this._camera) { this._camera.up.set(0, 1, 0); }
    this._scene = null;
  }
}
